import { fetchClient } from "../lib/openapi-query";
import type {
  LoginCredentials,
  RegisterCredentials,
  User,
} from "../types/auth";

export class AuthService {
  private static readonly TOKEN_KEY = "treel_token";
  private static readonly REFRESH_TOKEN_KEY = "treel_refresh_token";
  private static readonly USER_KEY = "treel_user";
  private static refreshInterval: NodeJS.Timeout | null = null;
  private static isRefreshing = false;
  private static refreshPromise: Promise<{
    accessToken: string;
    refreshToken: string;
  } | null> | null = null;

  static getStoredToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  static getStoredRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  static getStoredUser(): User | null {
    const userStr = localStorage.getItem(this.USER_KEY);

    const parsedUser = userStr
      ? (JSON.parse(userStr) as Record<string, unknown>)
      : null;

    if (!parsedUser || typeof parsedUser !== "object" || !parsedUser.id) {
      console.warn("Invalid user data in localStorage:", userStr);
      AuthService.logout().catch(console.error);
      return null;
    }

    return parsedUser as unknown as User;
  }

  static setAuth(token: string, user: User, refreshToken?: string): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    AuthService.setTokens(token, refreshToken);
    this.startTokenRefreshScheduler();
  }

  static setStoredUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  static setTokens(token: string, refreshToken?: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    if (refreshToken) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
    this.startTokenRefreshScheduler();
  }

  static clearAuth(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.stopTokenRefreshScheduler();
  }

  private static startTokenRefreshScheduler(): void {
    this.stopTokenRefreshScheduler();

    // Refresh token every 50 minutes (tokens typically expire in 1 hour)
    this.refreshInterval = setInterval(
      () => {
        const refreshToken = this.getStoredRefreshToken();
        if (refreshToken) {
          this.refreshAccessToken().catch((error) => {
            console.error("Scheduled token refresh failed:", error);
          });
        }
      },
      50 * 60 * 1000
    ); // 50 minutes
  }

  private static stopTokenRefreshScheduler(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  static async login(
    credentials: LoginCredentials
  ): Promise<{ user: User; token: string }> {
    try {
      const tokenResponse = await fetchClient.POST("/auth/login", {
        body: {
          email: credentials.email,
          password: credentials.password,
          force: true,
        },
      });

      if (tokenResponse.error) {
        throw new Error(tokenResponse.error.message ?? "Login failed");
      }

      this.setTokens(
        tokenResponse.data.accessToken,
        tokenResponse.data.refreshToken
      );

      // Set the token and get user profile
      const userResponse = await fetchClient.GET("/profile");

      if (userResponse.error) {
        throw new Error(userResponse.error.message ?? "Get Profile failed");
      }

      this.setAuth(
        tokenResponse.data.accessToken,
        userResponse.data,
        tokenResponse.data.refreshToken
      );
      return { user: userResponse.data, token: tokenResponse.data.accessToken };
    } catch (_error) {
      throw new Error("Login failed. Please check your credentials.");
    }
  }

  static async register(
    credentials: RegisterCredentials
  ): Promise<{ user: User; token: string }> {
    try {
      // Register first
      const registerResponse = await fetchClient.POST("/auth/register", {
        body: credentials,
      });

      if (registerResponse.error) {
        throw new Error(
          registerResponse.error.message ?? "Registration failed"
        );
      }

      // Then login to get tokens
      const loginResponse = await this.login({
        email: credentials.email,
        password: credentials.password,
      });

      return loginResponse;
    } catch (_error) {
      throw new Error("Registration failed. Please try again.");
    }
  }

  static async logout(): Promise<void> {
    try {
      const logoutResponse = await fetchClient.POST("/auth/logout");
      if (logoutResponse.error) {
        throw new Error("Logout failed");
      }
    } catch (error) {
      console.error("Logout request failed:", error);
    } finally {
      this.clearAuth();
    }
  }

  static async refreshAccessToken(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | null> {
    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getStoredRefreshToken();
    if (!refreshToken) {
      return null;
    }

    // Set the flag and create the promise
    this.isRefreshing = true;
    this.refreshPromise = this.doRefreshToken(refreshToken);

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      // Reset the flag and promise after completion
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private static async doRefreshToken(
    refreshToken: string
  ): Promise<{ accessToken: string; refreshToken: string } | null> {
    try {
      const response = await fetchClient.POST("/auth/refresh", {
        body: { refreshToken },
      });

      if (response.error) {
        // If refresh fails with 401, the session has expired
        if (response.response.status === 401) {
          console.error("Refresh token expired, redirecting to login");
          this.clearAuth();
          console.warn(
            "[DEBUG] Redirecting to login due to expired refresh token"
          );
          // window.location.href = "/login";
          return null;
        }
        throw new Error(response.error.message ?? "Token refresh failed");
      }

      // Update stored tokens
      this.setTokens(response.data.accessToken, response.data.refreshToken);

      return response.data;
    } catch (error) {
      console.error("Token refresh failed:", error);
      this.clearAuth();
      // Redirect to login on any refresh failure

      console.warn("[DEBUG] Redirecting to login due to token refresh failure");
      // window.location.href = "/login";
      return null;
    }
  }

  static async validateToken(): Promise<User | null> {
    const token = this.getStoredToken();
    if (!token) {
      return null;
    }

    try {
      const profileResponse = await fetchClient.GET("/profile");
      // Start scheduler if validation succeeds
      this.startTokenRefreshScheduler();
      if (profileResponse.error) {
        // If it's a 401, the interceptor will handle the refresh
        // Just return null here to avoid double refresh attempts
        if (profileResponse.response.status === 401) {
          return null;
        }
        throw new Error(
          profileResponse.error.message ?? "Profile validation failed"
        );
      }
      return profileResponse.data;
    } catch (error) {
      console.error("Token validation failed:", error);
      // The interceptor should have already handled auth refresh and redirect
      // Just return null here
      return null;
    }
  }
}
